import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { listResource, updateResource } from "@/lib/api";
import { fmtDate } from "@/components/common";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ymd = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";

// Runs that have a customer requested window but no scheduled date yet.
export const truckingWindowLabel = (r) => {
  if (!r.request_start) return "";
  return r.request_end && r.request_end !== r.request_start
    ? `${fmtDate(r.request_start)} – ${fmtDate(r.request_end)}`
    : fmtDate(r.request_start);
};

function RequestRow({ r }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const schedule = async (d) => {
    if (!d) return;
    try {
      await updateResource("trucking", r.id, { date: ymd(d) });
      qc.invalidateQueries({ queryKey: ["trucking"] });
      toast.success(`Scheduled for ${fmtDate(ymd(d))}`);
      setOpen(false);
    } catch {
      toast.error("Couldn't schedule");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="trucking-request-item"
          className="w-full text-left flex items-center justify-between gap-3 px-5 py-3 hover:bg-[#8B1E1E]/10 transition-colors">
          <div className="min-w-0">
            <div className="font-medium truncate">{r.customer || "—"}</div>
            <div className="text-sm text-gray-500 truncate">
              {[r.deliver_unit || r.pickup_unit, `needs: ${truckingWindowLabel(r)}`].filter(Boolean).join(" · ")}
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#8B1E1E]">
            Pick a date <ArrowRight className="w-4 h-4" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0 rounded-none border-2 border-gray-900">
        <div className="px-3 py-2 bg-[#16265F] text-white text-xs uppercase tracking-widest font-bold">Pick the scheduled day</div>
        <Calendar mode="single" numberOfMonths={1} onSelect={schedule} />
      </PopoverContent>
    </Popover>
  );
}

export default function TruckingRequests() {
  const { data } = useQuery({ queryKey: ["trucking"], queryFn: () => listResource("trucking"), refetchInterval: 5000 });
  const requests = (data || []).filter((r) => r.request_start && !r.date);
  if (!requests.length) return null;

  return (
    <div data-testid="trucking-requests" className="border-2 border-[#8B1E1E] bg-white">
      <div className="flex items-center gap-2 px-5 py-3 bg-[#8B1E1E] text-white">
        <CalendarClock className="w-5 h-5" />
        <span className="font-head font-bold uppercase tracking-wide">Requests to Schedule</span>
        <span className="ml-2 text-white/80 text-sm">{requests.length} waiting</span>
      </div>
      <div className="divide-y divide-gray-100">
        {requests.map((r) => <RequestRow key={r.id} r={r} />)}
      </div>
    </div>
  );
}
